/**
 * MapaVisitas (M10, RF-M10-002, RN-M10-003; spec mapa-contorno-brasil, design §6; ADR-032) — mapa client-side
 * dos pontos de visita em SVG, sobre o contorno das 27 UFs (IBGE 2024) quando ele está carregado.
 *
 * - Cada ponto tem coordenada VÁLIDA (o backend já exclui geom nula e geolocalização com marcador ERRO — PC-02);
 *   esta camada não reintroduz pontos inválidos. Clique num ponto navega para a ficha da escola (M7).
 * - Contorno e pontos usam a mesma projeção (`mapa/projecao.ts`); o enquadramento é o `viewBox`.
 * - Com uma UF em foco, o quadro enquadra a UF (as demais ficam esmaecidas) e pontos fora do quadro não são
 *   desenhados; no Brasil inteiro, as coordenadas são presas na caixa do país (PC-02).
 * - O contorno é só fundo: `aria-hidden` e sem eventos de ponteiro.
 * - ADR-038: a forma diz a evidência — "Com imagem" = círculo cheio, "Planilha (sem imagem)" = círculo vazado (a cor
 *   continua a do resultado) —, com a legenda e o tipo também no nome acessível do ponto.
 */

import { PontoMapa } from '../services/painel-types';
import { ROTULO_EVIDENCIA } from './Evidencia';
import {
  BBOX_BRASIL_PADRAO,
  ContornoUfs,
  projetarNoQuadro,
  quadroBrasil,
  quadroUf,
  raioNoQuadro,
  viewBox,
} from './mapa/projecao';

export interface MapaVisitasProps {
  readonly pontos: readonly PontoMapa[];
  /** Chamado ao clicar num ponto com INEP (navega para a ficha da escola M7). */
  readonly onSelecionar?: (inep: string) => void;
  /** Contorno das UFs; ausente durante a carga ou na falha (fundo liso). */
  readonly contorno?: ContornoUfs;
  /** O contorno não pôde ser carregado: mostra o aviso (os pontos continuam). */
  readonly contornoIndisponivel?: boolean;
  /** Sigla da UF a enquadrar; ignorada se não existir no contorno. */
  readonly ufEmFoco?: string;
}

/** Cor do ponto por resultado (produtiva/improdutiva/indefinido). */
function corDoResultado(resultado: string | null): string {
  const r = (resultado ?? '').toLowerCase();
  if (r === 'produtiva') return '#147d3f';
  if (r === 'improdutiva') return '#a4262c';
  return '#2a4b8d';
}

export function MapaVisitas({
  pontos,
  onSelecionar,
  contorno,
  contornoIndisponivel = false,
  ufEmFoco,
}: MapaVisitasProps): JSX.Element {
  const sigla = ufEmFoco?.trim().toUpperCase();
  const foco = sigla ? contorno?.ufs.find((u) => u.sigla === sigla) : undefined;
  const quadro = foco ? quadroUf(foco.bbox) : quadroBrasil(contorno?.brasil.bbox ?? BBOX_BRASIL_PADRAO);
  const raio = raioNoQuadro(quadro);

  const desenhados = pontos.flatMap((p, i) => {
    const pos = projetarNoQuadro(p.lng, p.lat, quadro);
    return pos === null ? [] : [{ p, i, pos }];
  });

  // A UF em foco vai por último, para a divisa destacada não ficar sob as vizinhas.
  const ufs = contorno
    ? [...contorno.ufs.filter((u) => u !== foco), ...(foco ? [foco] : [])]
    : [];

  return (
    <div>
      <svg
        className="mapa-svg"
        viewBox={viewBox(quadro)}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        role="group"
        aria-label={`Mapa de ${desenhados.length} visitas georreferenciadas${foco ? ` em ${foco.nome}` : ''}`}
      >
        <rect
          className="mapa-fundo"
          x={quadro.x}
          y={quadro.y}
          width={quadro.largura}
          height={quadro.altura}
          aria-hidden="true"
        />
        {ufs.length > 0 && (
          <g className="mapa-ufs" aria-hidden="true" pointerEvents="none">
            {ufs.map((u) => (
              <path
                key={u.sigla}
                d={u.d}
                data-uf={u.sigla}
                className={foco ? (u === foco ? 'uf uf--foco' : 'uf uf--esmaecida') : 'uf'}
                fillRule="evenodd"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        )}
        <g className="mapa-pontos">
          {desenhados.map(({ p, i, pos }) => {
            const clicavel = p.inep !== null && onSelecionar !== undefined;
            const vazado = p.evidencia === 'planilha';
            const cor = corDoResultado(p.resultado);
            const nome = p.nome ?? p.inep ?? 'Visita';
            const rotulo = p.evidencia ? `${nome} — ${ROTULO_EVIDENCIA[p.evidencia]}` : nome;
            return (
              <circle
                key={`${p.inep ?? 'sem-inep'}-${i}`}
                cx={pos.x}
                cy={pos.y}
                r={raio}
                fill={vazado ? 'none' : cor}
                stroke={vazado ? cor : '#fff'}
                strokeWidth={vazado ? '2' : '1'}
                vectorEffect="non-scaling-stroke"
                pointerEvents={vazado ? 'all' : undefined}
                style={clicavel ? { cursor: 'pointer' } : undefined}
                role={clicavel ? 'button' : undefined}
                aria-label={rotulo}
                onClick={clicavel ? () => onSelecionar!(p.inep as string) : undefined}
              >
                <title>{rotulo}</title>
              </circle>
            );
          })}
        </g>
      </svg>
      <p className="mapa-legenda" data-testid="legenda-evidencia">
        <span className="mapa-legenda-item">
          <span className="mapa-legenda-marca mapa-legenda-marca--cheia" aria-hidden="true" />
          <span>{ROTULO_EVIDENCIA.imagem}</span>
        </span>
        <span className="mapa-legenda-item">
          <span className="mapa-legenda-marca mapa-legenda-marca--vazada" aria-hidden="true" />
          <span>{ROTULO_EVIDENCIA.planilha}</span>
        </span>
      </p>
      {pontos.length === 0 && <p className="muted">Nenhuma visita com coordenada válida para exibir no mapa.</p>}
      {contornoIndisponivel && <p className="notice notice-info">Contorno do mapa indisponível.</p>}
      {contorno && <p className="mapa-fonte">Fonte: IBGE (Malha territorial 2024)</p>}
    </div>
  );
}
